/**
 * Transport abstraction.
 *
 * iOS Safari does not implement Web Bluetooth and Apple has declined to
 * (WebKit bug 101034 is RESOLVED WONTFIX; standards-position is `oppose`).
 * So the transport cannot be a foundation — it has to be a swappable adapter
 * with an honest fallback that always works.
 */

export type ConnectionState =
  | 'unsupported'
  | 'disconnected'
  | 'scanning'
  | 'connecting'
  | 'discovering'
  | 'connected'
  | 'reconnecting';

export type BleLogEntry = {
  at: number;
  dir: 'tx' | 'rx' | 'info' | 'error';
  text: string;
};

/** Device identifiers. Parameterised because this firmware uses a custom base. */
export type DeviceConfig = {
  serviceUuid: string;
  rxUuid: string;
  txUuid: string;
  namePrefix: string;
};

/**
 * Canonical Nordic UART. VERIFIED against firmware source 2026-08-09
 * (Documents/Arduino/PEMF_Controller/PEMF_Controller.ino, v4.2).
 *
 * Firmware v4.2 moved to the real NUS base so that ordinary BLE terminal apps
 * recognise the device. That matters far beyond tidiness: iOS Safari has no Web
 * Bluetooth, so a terminal app is the ONLY way an iPhone can talk to the
 * hardware, and those apps look for exactly these UUIDs.
 */
export const DEFAULT_DEVICE_CONFIG: DeviceConfig = {
  serviceUuid: '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
  rxUuid: '6e400002-b5a3-f393-e0a9-e50e24dcca9e',
  txUuid: '6e400003-b5a3-f393-e0a9-e50e24dcca9e',
  namePrefix: 'PEMF-Controller',
};

/**
 * The device's own Wi-Fi network, firmware 4.3 and newer.
 *
 * The device broadcasts this itself rather than joining a network, so it works
 * in a hotel or a car with no router and no internet. This is the iPhone route:
 * Safari has no Bluetooth, but it has no problem with a web page.
 *
 * The password is deliberately memorable rather than strong. It only has to
 * discourage a passer-by, and it has to be sayable out loud to someone else who
 * needs to start a session. Changeable per-device with the WIFIPASS command.
 */
export const DEVICE_WIFI = {
  ssid: 'PEMF-Controller',
  password: 'familypemf',
  url: 'http://192.168.4.1',
} as const;

/**
 * The pre-v4.2 base — real NUS in its first 32 bits only. Kept so a device that
 * has not been reflashed is still reachable from Settings.
 */
export const LEGACY_V41_CONFIG: DeviceConfig = {
  serviceUuid: '6e400001-b5b2-34fb-9ad1-6347d27d7011',
  rxUuid: '6e400002-b5b2-34fb-9ad1-6347d27d7011',
  txUuid: '6e400003-b5b2-34fb-9ad1-6347d27d7011',
  namePrefix: 'PEMF-Controller',
};

export type TransportEvents = {
  onState: (state: ConnectionState) => void;
  onMessage: (raw: string) => void;
  onLog: (entry: BleLogEntry) => void;
};

export interface BleTransport {
  readonly id: 'web-bluetooth' | 'manual';
  readonly label: string;
  /** False when this transport cannot run here at all (e.g. no Web Bluetooth). */
  readonly available: boolean;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  /** Write one command. Callers are responsible for inter-command pacing. */
  send(command: string): Promise<void>;
  readonly state: ConnectionState;
}
